import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Discrepancy } from "../api/types";

/**
 * Find all: every place a word or phrase is in the book (server: find.py), for
 * the report's Find mode - step through them all, across files, hearing each.
 */

/** A flag the proofing raised on a hit's words: enough to know it as the
 *  report's own (its review key and signature). */
export interface FindFlag {
  id: string;
  type: string;
  page: number | null;
  script_text: string;
  heard_text: string;
  start_time: number | null;
  end_time: number | null;
  manuscript_token_indices: number[];
  filtered: boolean;
}

export interface FindHit {
  id: string;
  chapter_id: string | null;     // null: a page no proofed file covers (nothing to hear)
  file: string | null;
  tokens: number[];              // the words, by the indices its file's report uses
  page: number | null;           // printed, as the report shows it
  pdf_page: number | null;       // the PDF's own page (for its picture)
  start: number | null;          // when it's heard
  end: number | null;
  before: string;
  text: string;
  after: string;
  flags: FindFlag[];
}

export interface FindResult {
  query: string;
  terms: string[];
  hits: FindHit[];
  truncated: boolean;
}

/** Longer selections aren't a search (server: MAX_WORDS). */
export const FIND_MAX_WORDS = 8;
export const findable = (text: string) => {
  const n = text.trim().split(/\s+/).filter(Boolean).length;
  return n > 0 && n <= FIND_MAX_WORDS;
};

// One cache entry per search however it was typed or selected.
const keyOf = (q: string) => q.trim().toLowerCase().replace(/[’‘]/g, "'").replace(/\s+/g, " ");

export function useFind(projectId: string | undefined, query: string, enabled = true) {
  const q = keyOf(query);
  return useQuery({
    queryKey: ["find", projectId, q],
    queryFn: () => api<FindResult>(`/api/modules/proofing/projects/${projectId}/find?q=${encodeURIComponent(query.trim())}`),
    enabled: enabled && !!projectId && !!q,
    staleTime: 60_000,
    retry: false,
  });
}

/** Where a search's words are on one PDF page, for its thumbnail. */
export function usePageFind(projectId: string, pdfPage: number | null, query: string, enabled: boolean) {
  return useQuery({
    queryKey: ["page-find", projectId, pdfPage, keyOf(query)],
    queryFn: () => api<{ boxes: [number, number, number, number][][] }>(
      `/api/modules/proofing/projects/${projectId}/manuscript-pages/${pdfPage}/find?q=${encodeURIComponent(query.trim())}`),
    enabled: enabled && pdfPage != null,
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/** The clip each hit plays: a moment before the words, a moment after. */
export const CLIP_BEFORE = 2;
export const CLIP_AFTER = 1;
export const clipOf = (h: FindHit): [number, number] | null =>
  h.start == null ? null : [Math.max(0, h.start - CLIP_BEFORE), (h.end ?? h.start + 0.6) + CLIP_AFTER];

/** Whether a flag lies on a hit's words. */
export const onWords = (idx: number[] | undefined, words: number[]) =>
  !!idx?.length && idx.some((i) => words.includes(i));

/** A find flag as the report's Discrepancy, for its review key and signature. */
export const asDiscrepancy = (f: FindFlag) => f as unknown as Discrepancy;
